// src/components/BankPosting/components/BankPostingUpdateAliasDialog.jsx
// ============================================================================
// BLOCK BP.CMP.008 — Update Customer Alias Dialog Component
// ----------------------------------------------------------------------------
// PURPOSE
// - Modal popup window to edit/update or remove customer alias matches
// - Pre-fills suggested text from selected bank transaction description
// - Ensures aliases saved here are scored at 100 in the alias matching engine
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK BP.CMP.008.01 — Main Dialog Component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders input fields for bank description / suggested alias text and customer info
// - Provides Update and Remove buttons as requested
// ============================================================================
function trimStr(value) {
  return String(value ?? '').trim();
}

function buildAliasKey(alias) {
  return [
    trimStr(alias?.ar_division_no || '00'),
    trimStr(alias?.customer_no),
    trimStr(alias?.alias_text),
  ].join('|');
}

function createAliasDialogDebugLogger({ token = 'BP_ALIAS_DIALOG' } = {}) {
  const debugValue =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('DEBUG') || ''
      : '';
  const runtimeNodeEnv =
    typeof window !== 'undefined' ? String(window?.globalConfig?.nodeEnv || '') : '';
  const debugList = String(debugValue)
    .split(',')
    .map((entry) => trimStr(entry))
    .filter(Boolean);
  const enabled = debugList.includes(token);
  const isProduction = runtimeNodeEnv.toLowerCase() === 'production';

  return (message, details) => {
    if (!enabled || isProduction) {
      return;
    }

    if (details !== undefined) {
      // eslint-disable-next-line no-console
      console.debug(`[${token}]`, message, details);
      return;
    }

    // eslint-disable-next-line no-console
    console.debug(`[${token}]`, message);
  };
}

const debugLog = createAliasDialogDebugLogger();

const selectableTextFieldSx = {
  '& .MuiInputBase-input': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    cursor: 'text',
  },
  '& .MuiInputBase-root': {
    cursor: 'text',
  },
};

export default function BankPostingUpdateAliasDialog({
  open,
  onClose,
  onUpdate,
  onRemove,
  onLoadAliases,
  defaultAliasText = '',
  defaultCustomerNo = '',
  defaultArDivisionNo = '00',
  defaultCustomerName = '',
  submitting = false,
}) {
  const [aliasText, setAliasText] = useState(defaultAliasText || '');
  const [customerNo, setCustomerNo] = useState(defaultCustomerNo || '');
  const [arDivisionNo, setArDivisionNo] = useState(defaultArDivisionNo || '00');
  const [customerName, setCustomerName] = useState(defaultCustomerName || '');
  const [errorMsg, setErrorMsg] = useState('');
  const [existingAliases, setExistingAliases] = useState([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);
  const [selectedExistingAliasKey, setSelectedExistingAliasKey] = useState('');

  const aliasLoadRequestRef = useRef(0);

  const selectedExistingAlias = useMemo(
    () => existingAliases.find((row) => buildAliasKey(row) === selectedExistingAliasKey) || null,
    [existingAliases, selectedExistingAliasKey]
  );

  const clearDialogState = useCallback(() => {
    setAliasText('');
    setCustomerNo('');
    setArDivisionNo('');
    setCustomerName('');

    setExistingAliases([]);
    setSelectedExistingAliasKey('');

    setErrorMsg('');
    setAliasesLoading(false);
  }, []);

  const loadExistingAliases = useCallback(
    async ({
      customerNo: requestedCustomerNo = '',
      arDivisionNo: requestedDivisionNo = '00',
    } = {}) => {
      const normalizedCustomerNo = trimStr(requestedCustomerNo);
      const normalizedDivisionNo = trimStr(requestedDivisionNo) || '00';
      const requestId = ++aliasLoadRequestRef.current;

      setExistingAliases([]);
      setSelectedExistingAliasKey('');
      setErrorMsg('');

      if (!normalizedCustomerNo || typeof onLoadAliases !== 'function') {
        setAliasesLoading(false);
        return [];
      }

      debugLog('Loading existing aliases', {
        customer_no: normalizedCustomerNo,
        ar_division_no: normalizedDivisionNo,
        requestId,
      });
      setAliasesLoading(true);

      try {
        const result = await onLoadAliases({
          customer_no: normalizedCustomerNo,
          ar_division_no: normalizedDivisionNo,
        });

        if (requestId !== aliasLoadRequestRef.current) {
          debugLog('Ignoring stale alias response', {
            requestId,
            currentRequestId: aliasLoadRequestRef.current,
          });
          return [];
        }

        const rows = Array.isArray(result)
          ? result
          : Array.isArray(result?.items)
            ? result.items
            : Array.isArray(result?.aliases)
              ? result.aliases
              : [];

        debugLog('Existing aliases loaded', {
          customer_no: normalizedCustomerNo,
          ar_division_no: normalizedDivisionNo,
          count: rows.length,
          requestId,
        });

        setExistingAliases(rows);
        return rows;
      } catch (err) {
        if (requestId !== aliasLoadRequestRef.current) {
          return [];
        }

        const message =
          err?.response?.data?.error || err?.message || 'Failed to load existing aliases.';
        debugLog('Existing aliases load failed', {
          customer_no: normalizedCustomerNo,
          ar_division_no: normalizedDivisionNo,
          message,
          error: err,
          requestId,
        });

        setExistingAliases([]);
        setErrorMsg(message);
        return [];
      } finally {
        if (requestId === aliasLoadRequestRef.current) {
          setAliasesLoading(false);
        }
      }
    },
    [onLoadAliases]
  );

  useEffect(() => {
    if (!open) {
      aliasLoadRequestRef.current += 1;
      clearDialogState();
      return;
    }

    aliasLoadRequestRef.current += 1;
    clearDialogState();

    const nextAliasText = trimStr(defaultAliasText);
    const nextCustomerNo = trimStr(defaultCustomerNo);
    const nextArDivisionNo = trimStr(defaultArDivisionNo) || '00';
    const nextCustomerName = trimStr(defaultCustomerName);

    setAliasText(nextAliasText);
    setCustomerNo(nextCustomerNo);
    setArDivisionNo(nextArDivisionNo);
    setCustomerName(nextCustomerName);

    if (nextCustomerNo) {
      loadExistingAliases({
        customerNo: nextCustomerNo,
        arDivisionNo: nextArDivisionNo,
      });
    }
  }, [
    open,
    defaultAliasText,
    defaultCustomerNo,
    defaultArDivisionNo,
    defaultCustomerName,
    clearDialogState,
    loadExistingAliases,
  ]);

  const handleExistingAliasSelect = (event) => {
    const key = event.target.value;
    setSelectedExistingAliasKey(key);

    const selected = existingAliases.find((row) => buildAliasKey(row) === key);
    if (!selected) return;

    setAliasText(trimStr(selected.alias_text));

    if (trimStr(selected.customer_name)) {
      setCustomerName(trimStr(selected.customer_name));
    }

    if (trimStr(selected.customer_no)) {
      setCustomerNo(trimStr(selected.customer_no));
    }

    if (trimStr(selected.ar_division_no)) {
      setArDivisionNo(trimStr(selected.ar_division_no));
    }

    setErrorMsg('');
  };

  const handleUpdateClick = async () => {
    const normalizedCustomerNo = trimStr(customerNo);
    const normalizedArDivisionNo = trimStr(arDivisionNo) || '00';
    const normalizedAliasText = trimStr(aliasText);

    if (!normalizedCustomerNo) {
      setErrorMsg('Customer Number is required.');
      return;
    }
    if (!normalizedAliasText) {
      setErrorMsg('Alias Text is required.');
      return;
    }

    const payload = {
      customer_no: normalizedCustomerNo,
      ar_division_no: normalizedArDivisionNo,
      customer_name: trimStr(customerName),
      alias_text: normalizedAliasText,
      ...(selectedExistingAlias?.id ? { id: selectedExistingAlias.id } : {}),
    };

    try {
      if (typeof onUpdate !== 'function') {
        throw new Error('Alias update handler is not configured.');
      }

      setErrorMsg('');
      debugLog('Submitting alias update', payload);

      await onUpdate(payload);
      debugLog('Alias update completed', payload);

      const refreshedAliases = await loadExistingAliases({
        customerNo: normalizedCustomerNo,
        arDivisionNo: normalizedArDivisionNo,
      });

      setExistingAliases(Array.isArray(refreshedAliases) ? refreshedAliases : []);
      setSelectedExistingAliasKey('');
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to update customer alias.';
      debugLog('Alias update failed', { payload, error: err, message });

      setErrorMsg(message);
    }
  };

  const handleRemoveClick = async () => {
    const aliasToRemove = selectedExistingAlias
      ? trimStr(selectedExistingAlias.alias_text)
      : trimStr(aliasText);
    const customerToRemove = selectedExistingAlias
      ? trimStr(selectedExistingAlias.customer_no)
      : trimStr(customerNo);
    const divisionToRemove =
      (selectedExistingAlias
        ? trimStr(selectedExistingAlias.ar_division_no)
        : trimStr(arDivisionNo)) || '00';

    if (!customerToRemove || !aliasToRemove) {
      setErrorMsg('Select an existing alias or enter Customer Number and Alias Text.');
      return;
    }

    if (!window.confirm(`Remove this alias?\n\n${aliasToRemove}`)) {
      return;
    }

    const payload = {
      ...(selectedExistingAlias?.id ? { id: selectedExistingAlias.id } : {}),
      customer_no: customerToRemove,
      ar_division_no: divisionToRemove,
      alias_text: aliasToRemove,
    };

    setErrorMsg('');
    debugLog('Removing alias', payload);
    await onRemove(payload);
    debugLog('Alias removed', payload);

    const refreshedAliases = await loadExistingAliases({
      customerNo: customerToRemove,
      arDivisionNo: divisionToRemove,
    });

    setExistingAliases(Array.isArray(refreshedAliases) ? refreshedAliases : existingAliases);
    setSelectedExistingAliasKey('');
    setAliasText(defaultAliasText || '');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Update Customer Alias</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Compare bank description with the alias table. Entered aliases are scored at{' '}
          <strong>100</strong> when matched.
        </Typography>

        {Boolean(errorMsg) && (
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'error.main' }}>
            {errorMsg}
          </Typography>
        )}

        <Stack spacing={2}>
          <TextField
            label="Suggested Text / Bank Description (Alias Text)"
            value={aliasText}
            onChange={(e) => {
              setAliasText(e.target.value);

              // Preserve the selected existing alias as the update target. If a caret row
              // is selected, this is an edit of that existing row rather than a brand-new alias.
            }}
            multiline
            rows={2}
            fullWidth
            required
            helperText="Suggested text to match against bank transaction descriptions"
            sx={selectableTextFieldSx}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="AR Division No"
              value={arDivisionNo}
              onChange={(e) => {
                setArDivisionNo(e.target.value);
                setSelectedExistingAliasKey('');
              }}
              size="small"
              sx={{ ...selectableTextFieldSx, width: { xs: '100%', sm: 140 } }}
            />

            <TextField
              label="Customer Number"
              value={customerNo}
              onChange={(e) => {
                setCustomerNo(e.target.value);
                setSelectedExistingAliasKey('');
              }}
              size="small"
              fullWidth
              required
              sx={selectableTextFieldSx}
            />
          </Stack>

          <TextField
            label="Customer Name (Optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            size="small"
            fullWidth
            sx={selectableTextFieldSx}
          />

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" fontWeight={600}>
                  Existing Aliases
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {customerNo ? `${customerNo} (${existingAliases.length})` : ''}
                </Typography>
                {aliasesLoading && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {!aliasesLoading && !existingAliases.length && (
                <Typography variant="caption" color="text.secondary">
                  No saved aliases for {customerNo || 'this customer'}.
                </Typography>
              )}

              {existingAliases.length > 0 && (
                <RadioGroup value={selectedExistingAliasKey} onChange={handleExistingAliasSelect}>
                  {existingAliases.map((alias) => {
                    const key = buildAliasKey(alias);

                    return (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ py: 0.25 }}>
                            <Typography variant="body2">{alias.alias_text}</Typography>
                            {alias.customer_name && (
                              <Typography variant="caption" color="text.secondary">
                                {alias.customer_name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button variant="outlined" color="error" onClick={handleRemoveClick} disabled={submitting}>
          Remove
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>

          <Button variant="contained" onClick={handleUpdateClick} disabled={submitting}>
            Update
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

BankPostingUpdateAliasDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onLoadAliases: PropTypes.func,
  defaultAliasText: PropTypes.string,
  defaultCustomerNo: PropTypes.string,
  defaultArDivisionNo: PropTypes.string,
  defaultCustomerName: PropTypes.string,
  submitting: PropTypes.bool,
};
